import React, { useEffect, forwardRef, useImperativeHandle, useRef, useState, useCallback, useMemo } from 'react';
import { Alert, Divider, Layout, List, Button, message, Modal, Checkbox, Input } from 'antd';
import BlocklyWorkspace, { BlocklyWorkspaceRef } from '../../Blockly/BlocklyWorkspace';
import PhaserView, { PhaserRef } from '../../Phaser';
import JsonEditor from '../../JsonEditor/JsonEditor';
import { EventManager } from '../../Phaser/engine/events/EventManager';
import { useChallengeConfig } from '../contexts/BlocklyConfigContext';
import { TSprite } from '../../../models/objects/sprite';
import { TObstacle, TScene } from '../../../models/objects/objects';
import { TSound } from '../../../models/objects/media';
import { CodeInterpreter, createCodeInterpreter } from '../services/codeInterpreter';
import { useExecutionContext } from '../contexts/ExecutionContext';
import DebugTB from '../toolbars/DebugTB';
import { mapImportedPhaserConfig } from '../../../utils/mapImportedPhaserConfig';
import stripPhaserAutoFields from '../../../utils/stripPhaserAutoFields';
import ResponsiveBlocklyPhaserLayout from '../ResponsiveBlocklyPhaserLayout';
import { DEFAULT_TOOLBOX_ITEMS } from '../../Blockly/assets/toolboxitems';
import { ToolboxCategory, ToolboxBlock } from '../../Blockly/types/config';
import { buildLessonAssetsFromPhaserConfig, mergeWithCatalog, LessonAssets } from '../../../utils/phaserLessonAssets';
import { useObjectsApi } from '../../../api/objects/objects';
const { Sider, Content } = Layout;


// Petite fonction de détection d'erreur
function isErrorLog(message: string): boolean {
    // Liste de mots-clés qu'on considère comme "erreur"
    const errorPatterns = [
        'erreur',                 // capter "Erreur..."
        'error',                  // capter "Error..."
        'non pris en charge',     // "Type de statement non pris en charge"
        'variable non déclarée',  // "Variable non déclarée..."
        // etc.
    ];

    const lowerMsg = message.toLowerCase();
    return errorPatterns.some((pattern) => lowerMsg.includes(pattern));
}

// On peut définir une interface pour le "type" de la ref que ce composant exposera :
export interface BlocklyContentRef {
    getWorkspaceXml: () => string | undefined;
    getPhaserConfig: () => any | undefined;
}

interface BlocklyContentProps {
    onWin: () => void;
    onSave?: (workspace: string) => void;
    /**
     * Determines whether admin controls (such as configuration import buttons
     * and the Phaser editor) should be visible.
     */
    adminMode?: boolean;
    /** Initial statement value for JsonEditor */
    initialStatement?: string;
    /** Callback when statement changes */
    onStatementChange?: (statement: string) => void;
}

/**
 * Composant qui gère :
 * - L'interpréteur asynchrone
 * - L'EventManager (Phaser + collisions, etc.)
 * - Expose `getWorkspaceXml()` via la ref
 */
const BlocklyContent = forwardRef<BlocklyContentRef, BlocklyContentProps>((props, ref) => {
    const { adminMode = false, onWin, onSave, initialStatement, onStatementChange } = props;
    const { getSpritesDetails, getScenes, getObstacles } = useObjectsApi();
    const workspaceRef = useRef<BlocklyWorkspaceRef>(null);

    const phaserRef = useRef<PhaserRef>(null);

    // Interpréteur
    const interpreterRef = useRef<CodeInterpreter | null>(null);

    // EventManager
    const eventManagerRef = useRef<EventManager | null>(null);

    // Extraction du contexte
    // Selon vos besoins, vous pouvez garder isRunning/isPaused si vous voulez
    // gérer un état global de "en cours" / "pause".
    const {
        logs,
        isRunning,
        isPaused,
        addLog,
        nextStep,
        stop,
        start,
        restart,
        stepBystepOff,
        stepBystepOn,
    } = useExecutionContext();

    // Config
    const { config, setConfig } = useChallengeConfig();

    const [importOpen, setImportOpen] = useState(false);
    const [importText, setImportText] = useState('');
    const [importBlocklyOpen, setImportBlocklyOpen] = useState(false);
    const [importBlocklyText, setImportBlocklyText] = useState('');
    const [importGeneralOpen, setImportGeneralOpen] = useState(false);
    const [importGeneralText, setImportGeneralText] = useState('');
    const [importedStatement, setImportedStatement] = useState<string>(initialStatement || '');
    const [toolboxModalOpen, setToolboxModalOpen] = useState(false);
    const [toolboxSelection, setToolboxSelection] = useState<Record<string, string[]>>({});
    const [toolboxSearch, setToolboxSearch] = useState('');
    const [expandedCategories, setExpandedCategories] = useState<string[]>([]);
    const [toolboxGrouped, setToolboxGrouped] = useState<boolean>(
        config.configWorkspace.toolboxGrouped ?? false
    );

    const currentCategoriesMap = useMemo(() => {
        const map = new Map<string, ToolboxCategory>();
        config.configWorkspace.toolboxCategories.forEach((category) => {
            map.set(category.name, category);
        });
        return map;
    }, [config.configWorkspace.toolboxCategories]);

    const initializeToolboxSelection = useCallback(() => {
        const selection: Record<string, string[]> = {};
        DEFAULT_TOOLBOX_ITEMS.forEach((category) => {
            const existingCategory = currentCategoriesMap.get(category.name);
            selection[category.name] = existingCategory
                ? existingCategory.blocks.map((block) => block.type)
                : [];
        });
        setToolboxSelection(selection);
    }, [currentCategoriesMap]);

    // Update importedStatement when initialStatement prop changes
    useEffect(() => {
        setImportedStatement(initialStatement || '');
    }, [initialStatement]);

    useEffect(() => {
        if (toolboxModalOpen) {
            initializeToolboxSelection();
            setToolboxSearch('');
            setExpandedCategories([]);
        }
    }, [toolboxModalOpen, initializeToolboxSelection]);

    useEffect(() => {
        if (!toolboxModalOpen) {
            return;
        }

        if (!toolboxSearch.trim()) {
            return;
        }

        const normalizedQuery = toolboxSearch.trim().toLowerCase();
        const matchingCategories = DEFAULT_TOOLBOX_ITEMS
            .filter((category) =>
                category.blocks.some((block) => block.type.toLowerCase().includes(normalizedQuery)),
            )
            .map((category) => category.name);

        setExpandedCategories((prev) => {
            if (prev.length === matchingCategories.length && prev.every((name) => matchingCategories.includes(name))) {
                return prev;
            }
            return matchingCategories;
        });
    }, [toolboxModalOpen, toolboxSearch]);

    const toggleCategoryExpansion = useCallback((categoryName: string) => {
        setExpandedCategories((prev) =>
            prev.includes(categoryName)
                ? prev.filter((name) => name !== categoryName)
                : [...prev, categoryName],
        );
    }, []);

    const handleToggleBlock = (categoryName: string, blockType: string, checked: boolean) => {
        setToolboxSelection((prev) => {
            const next = new Set(prev[categoryName] ?? []);
            if (checked) {
                next.add(blockType);
            } else {
                next.delete(blockType);
            }
            return {
                ...prev,
                [categoryName]: Array.from(next),
            };
        });
    };

    const handleToggleCategory = (categoryName: string, blockTypes: string[], checked: boolean) => {
        setToolboxSelection((prev) => ({
            ...prev,
            [categoryName]: checked ? [...blockTypes] : [],
        }));
    };

    const handleApplyToolboxSelection = () => {
        const updatedCategories: ToolboxCategory[] = [];
        const usedCategoryNames = new Set<string>();

        DEFAULT_TOOLBOX_ITEMS.forEach((category) => {
            const selectedTypes = new Set(toolboxSelection[category.name] ?? []);
            if (selectedTypes.size === 0) {
                return;
            }

            const existingCategory = currentCategoriesMap.get(category.name);
            const selectedBlocks = category.blocks
                .filter((block) => selectedTypes.has(block.type))
                .map((block) => {
                    const existingBlock = existingCategory?.blocks.find((b) => b.type === block.type);
                    return existingBlock ? { ...existingBlock } : { ...block };
                });

            const extraBlocks = (existingCategory?.blocks ?? []).filter(
                (block) => !category.blocks.some((defaultBlock) => defaultBlock.type === block.type),
            );

            const mergedBlocksMap = new Map<string, ToolboxBlock>();
            selectedBlocks.forEach((block) => mergedBlocksMap.set(block.type, { ...block }));
            extraBlocks.forEach((block) => mergedBlocksMap.set(block.type, { ...block }));

            const mergedBlocks = Array.from(mergedBlocksMap.values());
            if (mergedBlocks.length === 0) {
                return;
            }

            updatedCategories.push({
                name: category.name,
                color: existingCategory?.color ?? category.color,
                blocks: mergedBlocks,
            });
            usedCategoryNames.add(category.name);
        });

        const preservedCategories = config.configWorkspace.toolboxCategories.filter((category) => {
            if (usedCategoryNames.has(category.name)) {
                return false;
            }
            return !DEFAULT_TOOLBOX_ITEMS.some((defaultCategory) => defaultCategory.name === category.name);
        });

        const finalCategories = [...updatedCategories, ...preservedCategories];

        setConfig((prev) => ({
            ...prev,
            configWorkspace: {
                ...prev.configWorkspace,
                toolboxCategories: finalCategories,
                toolboxGrouped: toolboxGrouped, // 👈 Ajout important ici
            },
        }));

        setToolboxModalOpen(false);
        message.success('Toolbox mise à jour');
    };


    // Handler for statement changes
    const handleStatementChange = (newStatement: string) => {
        setImportedStatement(newStatement);
        onStatementChange?.(newStatement);
    };

    const [dbSprites, setDbSprites] = useState<TSprite[]>([]);
    const [dbScenes, setDbScenes] = useState<TScene[]>([]);
    const [dbObstacles, setDbObstacles] = useState<TObstacle[]>([]);
    const [sounds, setSounds] = useState<TSound[]>([]);
    const [catalog, setCatalog] = useState<Omit<LessonAssets, 'sounds'>>({
        sprites: [],
        scenes: [],
        obstacles: [],
    });

    // Catalogue d'assets de la base. Sans lui, le configurateur ne pouvait
    // proposer que les assets deja presents dans la lecon : sur une nouvelle
    // lecon la config Phaser est vide, donc les onglets Scene / Personnages /
    // Objets / Sons s'affichaient sans aucun choix.
    useEffect(() => {
        if (!adminMode) return;

        let cancelled = false;
        Promise.all([getSpritesDetails(), getScenes(), getObstacles()])
            .then(([spritesResp, scenesResp, obstaclesResp]) => {
                if (cancelled) return;
                setCatalog({
                    sprites: spritesResp.data || [],
                    scenes: scenesResp.data || [],
                    obstacles: obstaclesResp.data || [],
                });
            })
            .catch((err) => {
                message.error(err.response?.data ?? err.message ?? err);
            });

        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [adminMode]);

    // Au changement de config (ajout perso/objet/etc.), on relance
    useEffect(() => {
        const lessonAssets = buildLessonAssetsFromPhaserConfig(config.configPhaser);
        setDbSprites(mergeWithCatalog(lessonAssets.sprites, catalog.sprites, (s) => s.id, (s) => s.image_url));
        setDbScenes(mergeWithCatalog(lessonAssets.scenes, catalog.scenes, (s) => s.id, (s) => s.img));
        setDbObstacles(mergeWithCatalog(lessonAssets.obstacles, catalog.obstacles, (o) => o.id, (o) => o.img));
        // Les sons restent ceux de la lecon : l'onglet "Sons" du configurateur
        // interroge deja la base de son cote, inutile de tout precharger.
        setSounds(lessonAssets.sounds);
        restart();
    }, [config.configPhaser, catalog, restart]);

    // 1) Créer l'interpréteur + eventManager (une seule fois)
    useEffect(() => {
        const interpreter = createCodeInterpreter({
            isRunning,
            isPaused,
            currentStep: 0,
            nextStep,
            addLog,
            stop,
        });
        interpreterRef.current = interpreter;

        const eventManager = new EventManager();
        eventManagerRef.current = eventManager;

        // Pour debug global
        (window as any).eventManager = eventManager;

        const handleKeyDown = (e: KeyboardEvent) => {
            eventManager.dispatchKeyPressEvent(e.key);
        };

        document.addEventListener('keydown', handleKeyDown);

        return () => {
            document.removeEventListener('keydown', handleKeyDown);
            eventManager.clearAll();
            if (eventManagerRef.current === eventManager) {
                eventManagerRef.current = null;
            }
            if ((window as any).eventManager === eventManager) {
                delete (window as any).eventManager;
            }
        };

        // eslint-disable-next-line
    }, []);

    // 2) Surveille les changements de paused / running
    // (Optionnel : si votre CodeInterpreter gère un mode pause ou stop interne)
    useEffect(() => {
        interpreterRef.current?.setPaused(isPaused);
    }, [isPaused]);

    useEffect(() => {
        interpreterRef.current?.setRunning(isRunning);
    }, [isRunning]);

    /**
     * handleRun() : prévient le mode "stepBystep", nettoie l'eventManager,
     * génère le code et exécute.
     */
    const handleRun = async () => {
        // Nettoie collisions
        eventManagerRef.current?.clearAllCollisionListeners();

        // Sort du mode step-by-step si on y était
        stepBystepOff();

        // Sauvegarde du workspace
        if (onSave) {
            onSave(workspaceRef.current?.getWorkspaceXml() ?? '');
        }

        // Récupère le code
        if (!workspaceRef.current) {
            console.error('BlocklyWorkspace not available.');
            return;
        }
        if (!interpreterRef.current) {
            console.error('CodeInterpreter not initialized.');
            return;
        }

        const code = workspaceRef.current.getCode();

        // Initialise le code dans l'interpréteur
        interpreterRef.current.init(code);

        // Lance l’exécution asynchrone complète (sans le runLoop 300 ms)
        // Supposez que votre interpreter a une méthode .run() retournant une Promise
        try {
            start(); // Optionnel : on marque "isRunning = true" côté contexte
            await interpreterRef.current.runAll();
        } catch (err) {
            console.error('Execution error:', err);
        } finally {
        }
    };

    /**
     * handleNextStep() : 
     * Ici, vous pouvez soit :
     *   - conservez un mode stepping si votre interpreter gère encore .step() 
     *   - ou désactiver le bouton "Step" si vous n'en avez plus besoin
     * 
     * Exemple minimal : on exécute un step unique dans l'interpréteur.
     */
    const handleNextStep = () => {
        stepBystepOn();
        if (!isRunning) {
            start();
        }
        if (!workspaceRef.current || !interpreterRef.current) return;

        if (interpreterRef.current.hasMore()) {
            interpreterRef.current.step();
        } else {
            // On réinitialise et on refait un step
            eventManagerRef.current?.clearAllCollisionListeners();
            const code = workspaceRef.current.getCode();
            interpreterRef.current.init(code);
            interpreterRef.current.step();
        }
    };

    const handleRestart = () => {
        eventManagerRef.current?.clearAllCollisionListeners();
        restart();

    }

    const handlePreview = () => {
        try {
            const parsed = JSON.parse(importText || '{}');
            const mapped = mapImportedPhaserConfig(parsed, dbSprites, dbObstacles, dbScenes);
            setConfig((prev) => ({ ...prev, configPhaser: mapped }));
            message.success('Prévisualisation appliquée');
        } catch (err) {
            message.error('JSON invalide');
        }
    };

    const handlePreviewBlockly = () => {
        try {
            const parsed = JSON.parse(importBlocklyText || '{}');
            setConfig((prev) => ({ ...prev, configWorkspace: parsed }));
            message.success('Prévisualisation appliquée');
        } catch (err) {
            message.error('JSON invalide');
        }
    };

    const handlePreviewGeneral = () => {
        try {
            const parsed = JSON.parse(importGeneralText || '{}');
            const mapped = {
                ...parsed,
                configPhaser: mapImportedPhaserConfig(
                    parsed.configPhaser,
                    dbSprites,
                    dbObstacles,
                    dbScenes,
                ),
            };
            setConfig(mapped);
            message.success('Prévisualisation appliquée');
        } catch (err) {
            message.error('JSON invalide');
        }
    };

    const searchQuery = toolboxSearch.trim().toLowerCase();
    const categoriesContent = DEFAULT_TOOLBOX_ITEMS.reduce<React.ReactElement[]>((acc, category) => {
        const visibleBlocks = searchQuery
            ? category.blocks.filter((block) => block.type.toLowerCase().includes(searchQuery))
            : category.blocks;

        if (searchQuery && visibleBlocks.length === 0) {
            return acc;
        }

        const selectedForCategory = toolboxSelection[category.name] ?? [];
        const totalBlocksCount = category.blocks.length;
        const allSelected = totalBlocksCount > 0 && selectedForCategory.length === totalBlocksCount;
        const indeterminate = selectedForCategory.length > 0 && selectedForCategory.length < totalBlocksCount;
        const isExpanded = expandedCategories.includes(category.name);

        acc.push(
            <div key={category.name} style={{ borderBottom: '1px solid #f0f0f0' }}>
                <div
                    role="button"
                    tabIndex={0}
                    onClick={() => toggleCategoryExpansion(category.name)}
                    onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault();
                            toggleCategoryExpansion(category.name);
                        }
                    }}
                    style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '12px 0',
                        cursor: 'pointer',
                    }}
                >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ fontSize: 12, width: 12, textAlign: 'center' }}>{isExpanded ? '▾' : '▸'}</span>
                        <Checkbox
                            indeterminate={indeterminate}
                            checked={allSelected}
                            onClick={(e) => e.stopPropagation()}
                            onChange={(e) =>
                                handleToggleCategory(
                                    category.name,
                                    category.blocks.map((block) => block.type),
                                    e.target.checked,
                                )
                            }
                        >
                            {category.name}
                        </Checkbox>
                    </div>
                    <span style={{ color: 'rgba(0, 0, 0, 0.45)', fontVariantNumeric: 'tabular-nums' }}>
                        {selectedForCategory.length}/{totalBlocksCount}
                    </span>
                </div>
                {isExpanded && (
                    <div style={{ margin: '8px 0 12px 32px', display: 'flex', flexDirection: 'column', gap: 6 }}>
                        {visibleBlocks.map((block) => (
                            <div
                                key={block.type}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    padding: '4px 8px',
                                    borderRadius: 4,
                                    background: selectedForCategory.includes(block.type)
                                        ? 'rgba(24, 144, 255, 0.08)'
                                        : 'transparent',
                                }}
                            >
                                <Checkbox
                                    checked={selectedForCategory.includes(block.type)}
                                    onChange={(e) => handleToggleBlock(category.name, block.type, e.target.checked)}
                                >
                                    <span style={{ fontFamily: 'monospace' }}>{block.type}</span>
                                </Checkbox>
                            </div>
                        ))}
                        {visibleBlocks.length === 0 && (
                            <div style={{ color: 'rgba(0, 0, 0, 0.45)' }}>Aucun bloc correspondant.</div>
                        )}
                    </div>
                )}
            </div>,
        );

        return acc;
    }, []);

    const hasToolboxResults = categoriesContent.length > 0;

    // getWorkspaceXml exposé via ref
    const getWorkspaceXml = () => workspaceRef.current?.getWorkspaceXml();

    useImperativeHandle(ref, () => ({
        getWorkspaceXml,
        getPhaserConfig: () => phaserRef.current?.getConfig(),
    }));
    // === GESTION DE L'ALERT ERREUR EN MODE NON-ADMIN ===
    const [errorLogs, setErrorLogs] = useState<string[]>([]);
    const [showErrorAlert, setShowErrorAlert] = useState(false);

    useEffect(() => {
        // Filtrage : on retient tout log qui match isErrorLog
        const newErrorLogs = logs.filter(isErrorLog);

        if (newErrorLogs.length > 0) {
            setErrorLogs(newErrorLogs);
            setShowErrorAlert(true);

            // Cache l'alert après 3s
            const timer = setTimeout(() => {
                setShowErrorAlert(false);
            }, 3000);

            return () => clearTimeout(timer);
        } else {
            setShowErrorAlert(false);
        }
    }, [logs]);

    // Responsive logic for student mode
    const [screenSize, setScreenSize] = useState({
        width: window.innerWidth,
        height: window.innerHeight
    });

    useEffect(() => {
        const handleResize = () => {
            setScreenSize({
                width: window.innerWidth,
                height: window.innerHeight
            });
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const isMobile = screenSize.width < 768;
    // ResponsiveBlocklyPhaserLayout empile les colonnes en dessous de 1024px :
    // les zones internes doivent suivre le meme seuil, sinon leurs hauteurs en
    // vh debordent des sections empilees.
    const isStackedLayout = screenSize.width < 1024;
    const isDesktop = screenSize.width >= 1200;

    // For student mode, use responsive layout
    if (!adminMode) {
        const blocklyComponent = (
            <div className="tilmi-blockly-panel" style={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column'
            }}>
                <BlocklyWorkspace
                    ref={workspaceRef}
                    config={config.configWorkspace}
                    configPhaser={config.configPhaser}
                    height={isMobile ? '42vh' : (isStackedLayout ? '48vh' : '70vh')}
                />
                <div style={{ marginTop: 8, display: 'flex', justifyContent: 'flex-end' }}>
                    <DebugTB onRun={handleRun} onNextStep={handleNextStep} handleRestart={handleRestart} />
                </div>
            </div>
        );

        const phaserComponent = (
            <div style={{
                height: '100%',
                // Sans largeur explicite, ce bloc est un flex-item dimensionne par
                // son contenu : la scene pouvait retrecir mais jamais regrandir,
                // faute de largeur disponible a reprendre.
                width: '100%',
                minWidth: 0,
                position: 'relative',
                display: 'flex',
                flexDirection: 'column',
                padding: '8px',
                boxSizing: 'border-box',
                minHeight: 0,
                overflow: 'hidden',
            }}>
                {showErrorAlert && errorLogs.length > 0 && (
                    <Alert
                        style={{ position: 'absolute', top: 0, left: 0, width: '100%', zIndex: 1000 }}
                        message={errorLogs.map((err, idx) => (<div key={idx}>{err}</div>))}
                        type="error"
                        closable
                    />
                )}

                {eventManagerRef.current && (
                    <>
                        {/* Zone Phaser alignée en haut, même hauteur que Blockly */}
                        <div
                            style={{
                                // Empile : la hauteur decoule du ratio de la scene,
                                // un 70vh fige debordait de la section.
                                height: isStackedLayout ? 'auto' : '70vh',
                                width: '100%',
                                minHeight: 0,
                                minWidth: 0,
                                overflow: 'hidden',
                                display: 'flex',
                                alignItems: 'flex-start',   // 👈 aligne en haut
                                justifyContent: isStackedLayout ? 'center' : 'flex-start'
                            }}
                        >
                            <div style={{ height: isStackedLayout ? 'auto' : '70vh', width: '100%', minWidth: 0, position: 'relative' }}>
                                <PhaserView
                                    ref={phaserRef}
                                    projectMode={false}
                                    adminMode={adminMode}
                                    eventManager={eventManagerRef.current}
                                    config={config.configPhaser}
                                    isRunning={isRunning}
                                    onWin={onWin}
                                    sprites={dbSprites}
                                    scenesConfig={dbScenes}
                                    obstacles={dbObstacles}
                                    textsConfig={config.configPhaser.texts}
                                    sounds={sounds}
                                />
                            </div>
                        </div>
                    </>
                )}
            </div>
        );


        return (
            <ResponsiveBlocklyPhaserLayout
                className="tilmi-blockly-container-shell"
                style={{ height: isStackedLayout ? 'auto' : 'calc(100vh - 120px)' }}
                blocklyComponent={blocklyComponent}
                phaserComponent={phaserComponent}
            />
        );
    }

    // Admin mode - use original layout
    const animationWidth = config?.configPhaser?.scene?.width || 400;
    // En dessous de 1200px on empile : la colonne Phaser en pixels fixes ne
    // laissait plus qu'un filet de place au workspace, et le Sider a 600px
    // debordait de l'ecran sur mobile.
    const stacked = !isDesktop;
    const workspaceWidth = `calc(100% - ${animationWidth}px)`;

    return (
        <Layout
            style={{
                height: stacked ? 'auto' : '80vh',
                minHeight: stacked ? 0 : undefined,
                width: '100%',
                maxWidth: '100%',
                overflowX: 'hidden',
                display: 'flex',
                flexDirection: stacked ? 'column' : 'row',
            }}
        >
            <Sider
                width={stacked ? '100%' : workspaceWidth}
                style={{
                    background: '#fff',
                    padding: isMobile ? '8px' : '16px',
                    minWidth: 0,
                    maxWidth: '100%',
                    flex: stacked ? '0 0 auto' : undefined,
                }}
            >
                {adminMode && (
                    <div style={{ marginBottom: '16px', display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                        <Button size={isMobile ? 'small' : 'middle'} onClick={() => setImportOpen(true)}>Importer config Phaser</Button>
                        <Button size={isMobile ? 'small' : 'middle'} onClick={() => setImportBlocklyOpen(true)}>Importer config Blockly</Button>
                        <Button size={isMobile ? 'small' : 'middle'} onClick={() => setImportGeneralOpen(true)}>Importer config générale</Button>
                        <Button
                            size={isMobile ? 'small' : 'middle'}
                            onClick={() => {
                                initializeToolboxSelection();
                                setToolboxSearch('');
                                setToolboxModalOpen(true);
                            }}
                        >
                            Sélectionner les blocs
                        </Button>
                    </div>
                )}
                <BlocklyWorkspace
                    ref={workspaceRef}
                    config={config.configWorkspace}
                    configPhaser={config.configPhaser}
                    height={isMobile ? '46vh' : '70vh'}
                />

                <div style={{ marginTop: 8, display: 'flex', justifyContent: 'flex-end' }}>
                    <DebugTB onRun={handleRun} onNextStep={handleNextStep} handleRestart={handleRestart} />
                </div>
                {config.configWorkspace.adminMode && logs && (
                    <div style={{ marginTop: '16px' }}>
                        <h3>Logs</h3>
                        <List
                            size="small"
                            bordered
                            dataSource={logs}
                            renderItem={(item) => <List.Item>{item}</List.Item>}
                            style={{
                                maxHeight: '300px',
                                overflowY: 'auto',
                                background: '#fafafa',
                                padding: '8px',
                            }}
                        />
                    </div>
                )}
            </Sider>

            <Content
                style={{
                    background: '#fff',
                    position: 'relative',
                    marginTop: 15,
                    minHeight: 0,
                    minWidth: 0,
                    padding: isMobile ? '0 8px 8px' : undefined,
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: stacked ? 'center' : 'flex-start',
                    justifyContent: 'flex-start'
                }}
            >
                {/**
           * ALERT ERREURS - UNIQUEMENT EN MODE NON-ADMIN
           * Elle apparaît si on a détecté des errorLogs et que showErrorAlert === true
           */}
                {!config.configWorkspace.adminMode && showErrorAlert && errorLogs.length > 0 && (
                    <Alert
                        style={{ position: 'absolute', top: 0, left: 0, width: '100%', zIndex: 1000 }}
                        message={errorLogs.map((err, idx) => (
                            <div key={idx}>{err}</div>
                        ))}

                        type="error"
                        closable
                    />
                )}

                {eventManagerRef.current && (
                    <>
                        <div
                            style={{
                                // Empile : la scene prend la largeur dispo et sa
                                // hauteur decoule du ratio, pas d'un 70vh fixe.
                                height: stacked ? 'auto' : '70vh',
                                width: '100%',
                                minHeight: 0,
                                minWidth: 0,
                                overflow: 'hidden',
                                display: 'flex',
                                alignItems: 'flex-start',   // 👈 aligne en haut
                                justifyContent: stacked ? 'center' : 'flex-start'
                            }}
                        >
                            <div style={{ height: stacked ? 'auto' : '70vh', width: '100%', minWidth: 0, position: 'relative' }}>
                                <PhaserView
                                    ref={phaserRef}
                                    projectMode={false}
                                    adminMode={adminMode}
                                    eventManager={eventManagerRef.current}
                                    config={config.configPhaser}
                                    isRunning={isRunning}
                                    onWin={onWin}
                                    sprites={dbSprites}
                                    scenesConfig={dbScenes}
                                    obstacles={dbObstacles}
                                    textsConfig={config.configPhaser.texts}
                                    sounds={sounds}
                                />
                            </div>
                        </div>
                    </>
                )}
            </Content>
            <Modal
                title="Sélection des blocs Blockly"
                open={toolboxModalOpen}
                onCancel={() => setToolboxModalOpen(false)}
                onOk={handleApplyToolboxSelection}
                okText="Appliquer"
                cancelText="Annuler"
                width={720}
                destroyOnClose
            >
                <div style={{ marginBottom: 8 }}>
                    <Checkbox
                        checked={toolboxGrouped}
                        onChange={(e) => setToolboxGrouped(e.target.checked)}
                    >
                        Regrouper les blocs
                    </Checkbox>
                </div>

                <Input
                    placeholder="Rechercher un bloc..."
                    value={toolboxSearch}
                    onChange={(e) => setToolboxSearch(e.target.value)}
                    allowClear
                />
                <div style={{ marginTop: 16, maxHeight: '60vh', overflowY: 'auto' }}>
                    {hasToolboxResults ? (
                        categoriesContent
                    ) : (
                        <div style={{ padding: '16px 0', color: 'rgba(0, 0, 0, 0.45)' }}>
                            Aucun bloc ne correspond à votre recherche.
                        </div>
                    )}
                </div>
            </Modal>

            <Modal
                title="Importer une configuration Phaser"
                open={importOpen}
                onCancel={() => setImportOpen(false)}
                footer={null}
                width={800}
                destroyOnClose
            >
                <JsonEditor
                    title="Phaser Config"
                    value={stripPhaserAutoFields(config.configPhaser)}
                    onChangeText={setImportText}
                    onApply={(data) => {
                        const mapped = mapImportedPhaserConfig(
                            data,
                            dbSprites,
                            dbObstacles,
                            dbScenes,
                        );
                        setConfig((prev) => ({ ...prev, configPhaser: mapped }));
                        setImportOpen(false);
                        message.success('Configuration importée');
                    }}
                    extraActions={<Button onClick={handlePreview}>Prévisualiser</Button>}
                />
            </Modal>
            <Modal
                title="Importer une configuration Blockly"
                open={importBlocklyOpen}
                onCancel={() => setImportBlocklyOpen(false)}
                footer={null}
                width={800}
                destroyOnClose
            >
                <JsonEditor
                    title="Blockly Config"
                    value={config.configWorkspace}
                    onChangeText={setImportBlocklyText}
                    onApply={(data) => {
                        // Imported data includes workspace and teacherWorkspace (if present)
                        setConfig((prev) => ({ ...prev, configWorkspace: data }));
                        setImportBlocklyOpen(false);
                        message.success('Configuration importée');
                    }}
                    extraActions={<Button onClick={handlePreviewBlockly}>Prévisualiser</Button>}
                />
            </Modal>
            <Modal
                title="Importer une configuration générale"
                open={importGeneralOpen}
                onCancel={() => setImportGeneralOpen(false)}
                footer={null}
                width={800}
                destroyOnClose
            >
                <JsonEditor
                    title="Configuration générale"
                    value={{ ...config, configPhaser: stripPhaserAutoFields(config.configPhaser) }}
                    onChangeText={setImportGeneralText}
                    showStatement={true}
                    initialStatement={importedStatement}
                    onStatementChange={handleStatementChange}
                    onApply={(data) => {
                        // Merge imported data (includes configWorkspace with workspace and teacherWorkspace)
                        const mapped = {
                            ...data,
                            configPhaser: mapImportedPhaserConfig(
                                data.configPhaser,
                                dbSprites,
                                dbObstacles,
                                dbScenes,
                            ),
                        };
                        // configWorkspace includes workspace and teacherWorkspace (if present in imported JSON)
                        setConfig(mapped);
                        setImportGeneralOpen(false);
                        message.success('Configuration importée');
                    }}
                    extraActions={<Button onClick={handlePreviewGeneral}>Prévisualiser</Button>}
                />
            </Modal>
        </Layout>
    );
});

export default BlocklyContent;
